import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { orderBy, isEqual } from 'lodash'
import { API, graphqlOperation } from 'aws-amplify'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'
import { makeErrorToast, msg, makeSuccessToast } from '../../utils/makeToast'
import i18next from 'i18next'
import { fetchSuppliersByTenantIdUtil } from '@src/utils/fetchUtils'
import { DataStore } from '@aws-amplify/datastore'
import { Suppliers } from '../../models'

// Fetches contacts that has users tenantId and the active flag is true
export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async (arg, { getState, rejectWithValue }) => {
    const { auth, settings } = getState()
    try {
      const suppliersData = await DataStore.query(Suppliers, item =>
        item.tenantId('eq', auth.userInfo.tenantId).activeFlag('eq', true)
      )
      const parsedSuppliersData = JSON.parse(JSON.stringify(suppliersData))

      return parsedSuppliersData
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const getSupplierDetails = createAsyncThunk(
  'suppliers/getSupplierDetails',
  async (supplierId, { getState, rejectWithValue }) => {
    console.log('supplierId: ', supplierId)
    const { auth, settings } = getState()
    try {
      const {
        data: { getSuppliers: items },
      } = await API.graphql(
        graphqlOperation(queries.getSuppliers, {
          id: supplierId,
        })
      )
      return items
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Creates a new contact item with info provided by user and then fetches contacts again
export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (supplierInfo, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState()
      let newSupplier = {
        id: nanoid(),
        tenantId: auth.userInfo.tenantId,
        activeFlag: true,
        ...supplierInfo,
      }

      console.log('newSupplier: ', newSupplier)
      await API.graphql(graphqlOperation(mutations.createSuppliers, { input: newSupplier }))
      makeSuccessToast(i18next.t('suppliers:toasts.addedSupplier'))
      dispatch(fetchSuppliers())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Updates the contacts & fetches the contacts again
export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async ({ supplierId, newSupplier }, { dispatch, rejectWithValue }) => {
    console.log('newSupplier: ', newSupplier)
    console.log('supplierId: ', supplierId)
    try {
      const {
        data: { getSuppliers },
      } = await API.graphql(graphqlOperation(queries.getSuppliers, { id: supplierId }))
      let updateSupplierParms = {}
      updateSupplierParms.id = getSuppliers.id
      updateSupplierParms._version = getSuppliers._version
      for (const property in newSupplier) {
        if (!isEqual(newSupplier[property], getSuppliers[property])) {
          updateSupplierParms[property] = newSupplier[property]
        }
      }
      delete updateSupplierParms._deleted
      delete updateSupplierParms.createdOn
      delete updateSupplierParms.updatedOn
      delete updateSupplierParms._lastChangedAt
      await API.graphql(graphqlOperation(mutations.updateSuppliers, { input: updateSupplierParms }))
      makeSuccessToast(i18next.t('suppliers:toasts.updatedSupplier'))
      dispatch(fetchSuppliers())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Deletes the contacts from id & fetches the contacts again
export const deleteSupplier = createAsyncThunk(
  'supplier/deleteSupplier',
  async ({ supplierInfo }, { dispatch, rejectWithValue }) => {
    console.log('supplierInfo: ', supplierInfo)
    try {
      await API.graphql(
        graphqlOperation(mutations.updateSuppliers, {
          input: { id: supplierInfo.id, _version: supplierInfo._version, activeFlag: false },
        })
      )
      makeSuccessToast(i18next.t('suppliers:toasts.deletedSupplier'))
      dispatch(fetchSuppliers())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updateArchiveStatus = createAsyncThunk(
  'suppliers/updateArchiveStatus',
  async ({ currentSupplierId }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getSuppliers },
      } = await API.graphql(graphqlOperation(queries.getSuppliers, { id: currentSupplierId }))

      let updatedSupplier = {
        id: getSuppliers.id,
        _version: getSuppliers._version,
        isArchived: !getSuppliers.isArchived,
      }
      await API.graphql(graphqlOperation(mutations.updateSuppliers, { input: updatedSupplier }))

      dispatch(fetchSuppliers())
      makeSuccessToast(
        getSuppliers.isArchived
          ? i18next.t('suppliers:toasts.unarchiveSupplier')
          : i18next.t('suppliers:toasts.archiveSupplier')
      )
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const supplierSlice = createSlice({
  name: 'suppliers',
  initialState: {
    suppliersData: [],
    fetchSupplierApiStatus: 'idle',
    updateArchiveApiStatus: 'idle',
    createOrUpdateSupplierApiStatus: 'idle',
    viewSupplierStatus: 'idle',
    deleteSupplierApiStatus: 'idle',
    createSupplierModalOpen: false,
    supplierArchivedConfirmationDialogOpenStatus: false,
    viewSupplierOpen: false,
    supplierDeleteDialogOpenStatus: false,
    currentSupplier: null,
    supplierEditModalStatus: { isOpen: false },
    editSupplierMenuAnchorEl: null,
    error: null,
  },
  reducers: {
    setCreateSupplierModalOpen: (state, action) => {
      state.createSupplierModalOpen = action.payload.status
    },
    setViewSupplierOpen: (state, action) => {
      state.viewSupplierOpen = action.payload.status
      if (!action.payload.status) {
        state.currentSupplier = null
      }
    },
    setSupplierDeleteDialogOpenStatus: (state, action) => {
      state.supplierDeleteDialogOpenStatus = action.payload.status
      if (!action.payload.status) {
        state.currentSupplier = null
      }
    },
    setEditSupplierMenuAnchorEl: (state, action) => {
      state.editSupplierMenuAnchorEl = action.payload
    },
    setSupplierEditModalStatus: (state, action) => {
      state.supplierEditModalStatus = action.payload
    },
    setCurrentSupplier: (state, action) => {
      state.currentSupplier = action.payload
    },
    setSupplierArchivedConfirmationDialogOpenStatus: (state, action) => {
      state.supplierArchivedConfirmationDialogOpenStatus = action.payload.status
    },
  },
  extraReducers: {
    [fetchSuppliers.pending]: (state, action) => {
      state.fetchSupplierApiStatus = 'loading'
    },
    [fetchSuppliers.fulfilled]: (state, action) => {
      state.fetchSupplierApiStatus = 'succeeded'
      state.suppliersData = action.payload
    },
    [fetchSuppliers.rejected]: (state, action) => {
      state.fetchSupplierApiStatus = 'failed'
      state.error = action.error.message
    },
    [createSupplier.pending]: (state, action) => {
      state.createOrUpdateSupplierApiStatus = 'loading'
    },
    [createSupplier.fulfilled]: (state, action) => {
      state.createOrUpdateSupplierApiStatus = 'succeeded'
      state.createSupplierModalOpen = false
    },
    [createSupplier.rejected]: (state, action) => {
      state.createOrUpdateSupplierApiStatus = 'failed'
      state.createSupplierModalOpen = false
      state.error = action.error.message
    },
    [getSupplierDetails.pending]: (state, action) => {
      state.viewSupplierStatus = 'loading'
    },
    [getSupplierDetails.fulfilled]: (state, action) => {
      state.viewSupplierStatus = 'succeeded'
      state.currentSupplier = action.payload
    },
    [getSupplierDetails.rejected]: (state, action) => {
      state.viewSupplierStatus = 'failed'
      state.currentSupplier = null
      state.error = action.error.message
    },
    [updateSupplier.pending]: (state, action) => {
      state.createOrUpdateSupplierApiStatus = 'loading'
    },
    [updateSupplier.fulfilled]: (state, action) => {
      state.createOrUpdateSupplierApiStatus = 'succeeded'
      state.supplierEditModalStatus = false
      state.currentSupplier = null
    },
    [updateSupplier.rejected]: (state, action) => {
      state.createOrUpdateSupplierApiStatus = 'failed'
      state.supplierEditModalStatus = false
      state.error = action.error.message
    },
    [updateArchiveStatus.pending]: (state, action) => {
      state.updateArchiveApiStatus = 'loading'
    },
    [updateArchiveStatus.fulfilled]: (state, action) => {
      state.updateArchiveApiStatus = 'succeeded'
      state.supplierArchivedConfirmationDialogOpenStatus = false
    },
    [updateArchiveStatus.rejected]: (state, action) => {
      state.updateArchiveApiStatus = 'failed'
      state.error = action.error.message
    },
    [deleteSupplier.pending]: (state, action) => {
      state.deleteSupplierApiStatus = 'loading'
    },
    [deleteSupplier.fulfilled]: (state, action) => {
      state.deleteSupplierApiStatus = 'succeeded'
      state.SupplierDeleteDialogOpenStatus = false
    },
    [deleteSupplier.rejected]: (state, action) => {
      state.deleteSupplierApiStatus = 'failed'
      state.dupplierDeleteDialogOpenStatus = false
      state.error = action.error.message
    },
  },
})

export const {
  setCreateSupplierModalOpen,
  setViewSupplierOpen,
  setSupplierDeleteDialogOpenStatus,
  setEditSupplierMenuAnchorEl,
  setSupplierEditModalStatus,
  setCurrentSupplier,
  setSupplierArchivedConfirmationDialogOpenStatus,
} = supplierSlice.actions

export default supplierSlice.reducer
